import * as Sentry from '@sentry/react'
import packageJson from '../../package.json'

/**
 * App version from package.json - used for release tracking
 */
export const APP_VERSION = packageJson.version

/**
 * Initialize Sentry for error tracking and performance monitoring
 * 
 * This should be called before React renders the app.
 * The DSN (Data Source Name) is required and should be set via environment variable.
 * 
 * To get your DSN:
 * 1. Go to https://sentry.io
 * 2. Create a project or select an existing one
 * 3. Go to Settings > Client Keys (DSN)
 * 4. Copy your DSN and add it to your .env file as VITE_SENTRY_DSN
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.warn(
      'Sentry DSN not found. Error tracking is disabled. ' +
      'Set VITE_SENTRY_DSN in your .env file to enable Sentry.'
    )
    return
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in dev
    // Session Replay
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in dev
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Environment - Use Vercel's env if available, otherwise fall back to Vite's mode
    // VITE_VERCEL_ENV: 'production' | 'preview' | 'development' (set by Vercel)
    // MODE: 'development' | 'production' (set by Vite based on build command)
    environment: import.meta.env.VITE_VERCEL_ENV || import.meta.env.MODE,
    
    // Release tracking - uses package.json version directly
    release: `calintent-web@${APP_VERSION}`,
    
    // Filter out localhost errors in production (optional)
    beforeSend(event) {
      // Don't send errors from localhost in production
      if (import.meta.env.PROD && window.location.hostname === 'localhost') {
        return null
      }
      return event
    },
    
    // Ignore certain errors (optional)
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
    ],
  })
}

